/**
 * Connector icon resolution — the single place that decides which image a
 * connector shows, so a card never points at a file that does not exist.
 *
 * Order: an explicit local logo, then the brand asset we ship under
 * `/logos`, then an icon URL the server sent, then a generic category icon.
 * `ConnectorLogo` adds a last-resort swap to the default icon if the chosen
 * URL still fails to load.
 */

export type ConnectorIconKind =
  | "database"
  | "warehouse"
  | "bi"
  | "crm"
  | "files"
  | "analytics"
  | "app";

const LOGOS_DIR = "/logos";
const GENERIC_ICONS_DIR = `${LOGOS_DIR}/generic`;

export const CONNECTOR_ICON_KINDS: readonly ConnectorIconKind[] = [
  "database",
  "warehouse",
  "bi",
  "crm",
  "files",
  "analytics",
  "app",
];

export const genericConnectorIconUrl = (kind: ConnectorIconKind): string =>
  `${GENERIC_ICONS_DIR}/${kind}.svg`;

/** Shown when nothing else is known, and when any icon fails to load. */
export const DEFAULT_CONNECTOR_ICON_URL = genericConnectorIconUrl("app");

/** `Google_Sheets`, `google-sheets` and `Google Sheets` all become `googlesheets`. */
export const normalizeConnectorKey = (value: string): string =>
  value.toLowerCase().replace(/[\s_-]+/g, "");

/**
 * Brand assets shipped in `public/logos`, by normalized connector key. Only
 * list a file that exists there — a test checks every entry against the
 * directory.
 */
export const CONNECTOR_BRAND_LOGO_FILES: Readonly<Record<string, string>> = {
  airtable: "airtable.svg",
  amplitude: "amplitude.svg",
  asana: "asana.svg",
  azure: "azure.svg",
  bigquery: "bigquery.svg",
  clickhouse: "clickhouse.svg",
  confluence: "confluence.svg",
  csv: "csv.svg",
  databricks: "databricks.svg",
  domo: "domo.svg",
  dropbox: "dropbox.svg",
  excel: "excel.svg",
  freshdesk: "freshdesk.svg",
  ga4: "google-analytics.svg",
  gong: "gong.svg",
  googleads: "google-ads.svg",
  googlebigquery: "bigquery.svg",
  googlesheets: "google_sheets.svg",
  greenhouse: "greenhouse.svg",
  heap: "heap.svg",
  hubspot: "hubspot.svg",
  intercom: "intercom.svg",
  jira: "jira.svg",
  linear: "linear.svg",
  looker: "looker.svg",
  mailchimp: "mailchimp.svg",
  mariadb: "mariadb.svg",
  metabase: "metabase.svg",
  metaads: "meta.svg",
  mixpanel: "mixpanel.svg",
  monday: "monday.svg",
  mongodb: "mongodb.svg",
  mysql: "mysql.svg",
  netsuite: "netsuite.svg",
  notion: "notion.svg",
  oracle: "oracle.svg",
  pdf: "adobe-acrobat.svg",
  pipedrive: "pipedrive.svg",
  postgres: "postgresql.svg",
  postgresql: "postgresql.svg",
  posthog: "posthog.svg",
  powerbi: "powerbi.svg",
  presto: "presto.svg",
  qlik: "qlik.svg",
  quickbooks: "quickbooks.svg",
  redis: "redis.svg",
  redshift: "redshift.svg",
  s3: "s3.svg",
  salesforce: "salesforce.svg",
  segment: "segment.svg",
  shopify: "shopify.svg",
  sigma: "sigma.svg",
  slack: "slack.svg",
  snowflake: "snowflake.svg",
  sqlserver: "sqlserver.svg",
  stripe: "stripe.svg",
  superset: "superset.svg",
  synapse: "azure-synapse.svg",
  tableau: "tableau.svg",
  teradata: "teradata.svg",
  workday: "workday.svg",
  xlsx: "excel.svg",
  zendesk: "zendesk.svg",
  zohocrm: "zoho.svg",
};

/**
 * Connectors listed on a card that no brand file covers yet. Their card shows
 * the generic category icon. A test requires every connector key to be either
 * in `CONNECTOR_BRAND_LOGO_FILES` or here, so a new source cannot slip through
 * with a generic icon unnoticed. Drop the file into `public/logos` (see
 * `ATTRIBUTION.md` there), map it above, and remove the key from this list.
 */
export const CONNECTORS_WITHOUT_BRAND_LOGO: readonly string[] = ["mode"];

/**
 * Brand files whose mark is dark enough to vanish on a dark surface. The logo
 * frame puts a light tile behind these in the dark theme only.
 */
const LOGO_FILES_NEEDING_DARK_BACKDROP: ReadonlySet<string> = new Set([
  "heap.svg",
  "intercom.svg",
  "linear.svg",
  "mailchimp.svg",
  "mysql.svg",
  "netsuite.svg",
  "notion.svg",
  "pipedrive.svg",
  "sqlserver.svg",
  "superset.svg",
  "workday.svg",
]);

/** Whether the image at `src` needs a light backdrop in the dark theme. */
export const connectorLogoNeedsDarkBackdrop = (src: string): boolean => {
  const file = src.split(/[?#]/)[0].split("/").pop() ?? "";
  return (
    src.startsWith(`${LOGOS_DIR}/`) && LOGO_FILES_NEEDING_DARK_BACKDROP.has(file)
  );
};

/** The category a connector without a brand asset is drawn with. */
export const CONNECTOR_ICON_KIND_BY_KEY: Readonly<
  Record<string, ConnectorIconKind>
> = {
  domo: "bi",
  freshdesk: "app",
  mode: "bi",
  netsuite: "app",
  pdf: "files",
  sigma: "bi",
  sqlserver: "database",
  synapse: "warehouse",
  workday: "app",
};

/** Marketplace category type (see `data/connectors.ts`) to icon kind. */
export const CONNECTOR_ICON_KIND_BY_CATEGORY: Readonly<
  Record<string, ConnectorIconKind>
> = {
  ANALYTICS: "analytics",
  APP: "app",
  BI: "bi",
  CRM: "crm",
  DATABASE: "database",
  DATA_WAREHOUSE: "warehouse",
  FILES: "files",
  PRODUCTIVITY: "app",
};

export interface ResolveConnectorIconInput {
  /** Connector key in any casing or separator style. */
  catalogKey: string;
  /** Marketplace category type, used for the generic icon when the key is unknown. */
  categoryType?: string;
  /** A logo bundled with the local connector catalog. */
  localLogo?: string | null;
  /** An icon URL sent by the server. */
  serverIconUrl?: string | null;
}

const nonEmpty = (value: string | null | undefined): string | undefined =>
  value && value.trim().length > 0 ? value : undefined;

export const resolveConnectorIconUrl = ({
  catalogKey,
  categoryType,
  localLogo,
  serverIconUrl,
}: ResolveConnectorIconInput): string => {
  const key = normalizeConnectorKey(catalogKey);
  const brandFile = CONNECTOR_BRAND_LOGO_FILES[key];
  const kind =
    CONNECTOR_ICON_KIND_BY_KEY[key] ??
    (categoryType ? CONNECTOR_ICON_KIND_BY_CATEGORY[categoryType] : undefined);

  return (
    nonEmpty(localLogo) ??
    (brandFile ? `${LOGOS_DIR}/${brandFile}` : undefined) ??
    nonEmpty(serverIconUrl) ??
    (kind ? genericConnectorIconUrl(kind) : DEFAULT_CONNECTOR_ICON_URL)
  );
};
